import { useAuth } from "@elit-hair/elit-tools";
import { type ReactNode, createContext, useContext, useMemo } from "react";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { useUserScopedLocalStorage } from "@/hooks/use-user-scoped-local-storage";

import { ROLE_PERMISSION_PRESETS } from "../constants/role-permission-presets";
import { DASHBOARD_ROLES, type DashboardRole, type RolePreviewState } from "../types/dashboard-role.types";
import { resolveDashboardRole } from "../utils/resolve-dashboard-role";
import {
    canUseRolePreview,
    normalizeRolePreviewState,
    permissionsMatchPreset,
    toggleIdInList,
    togglePermissionInList,
} from "../utils/role-preview.utils";

type DashboardRoleContextValue = {
    role: DashboardRole;
    permissionCodes: string[];
    teamIds: string[];
    setPreviewRole: (role: DashboardRole) => void;
    togglePermission: (permissionCode: string) => void;
    toggleTeam: (teamId: string) => void;
    setTeams: (teamIds: string[]) => void;
    clearPreview: () => void;
    isPreview: boolean;
};

const DashboardRoleContext = createContext<DashboardRoleContextValue | null>(null);

export function DashboardRoleProvider({ children }: { children: ReactNode }) {
    const { userData } = useAuth();
    const realPermissionCodes = userData?.permissionCodes ?? [];
    const [storedPreview, setStoredPreview] = useUserScopedLocalStorage<unknown>(
        LOCAL_STORAGE_KEYS.DASHBOARD_ROLE_PREVIEW.key,
        LOCAL_STORAGE_KEYS.DASHBOARD_ROLE_PREVIEW.default
    );

    const previewState = useMemo(() => {
        if (!canUseRolePreview(realPermissionCodes)) {
            return null;
        }

        return normalizeRolePreviewState(storedPreview);
    }, [realPermissionCodes, storedPreview]);

    const permissionCodes = previewState ? previewState.permissions : realPermissionCodes;
    const teamIds = previewState?.teamIds ?? [];
    const resolvedRole = resolveDashboardRole(permissionCodes);
    const role =
        previewState?.role && permissionsMatchPreset(permissionCodes, previewState.role)
            ? previewState.role
            : resolvedRole;
    const isPreview = previewState !== null;

    const setPreviewRole = (nextRole: DashboardRole) => {
        const nextState: RolePreviewState = {
            permissions: [...ROLE_PERMISSION_PRESETS[nextRole]],
            teamIds: previewState?.teamIds ?? [],
            role: nextRole,
        };
        setStoredPreview(nextState);
    };

    const togglePermission = (permissionCode: string) => {
        const basePermissions = previewState?.permissions ?? realPermissionCodes;
        const nextPermissions = togglePermissionInList(basePermissions, permissionCode);
        const nextState: RolePreviewState = {
            permissions: nextPermissions,
            teamIds: previewState?.teamIds ?? [],
            role: resolveDashboardRole(nextPermissions),
        };
        setStoredPreview(nextState);
    };

    const toggleTeam = (teamId: string) => {
        const basePermissions = previewState?.permissions ?? realPermissionCodes;
        const nextState: RolePreviewState = {
            permissions: [...basePermissions],
            teamIds: toggleIdInList(previewState?.teamIds ?? [], teamId),
            role: previewState?.role,
        };
        setStoredPreview(nextState);
    };

    const setTeams = (nextTeamIds: string[]) => {
        const basePermissions = previewState?.permissions ?? realPermissionCodes;
        const nextState: RolePreviewState = {
            permissions: [...basePermissions],
            teamIds: [...new Set(nextTeamIds)],
            role: previewState?.role,
        };
        setStoredPreview(nextState);
    };

    const clearPreview = () => {
        setStoredPreview(null);
    };

    return (
        <DashboardRoleContext.Provider
            value={{
                role,
                permissionCodes,
                teamIds,
                setPreviewRole,
                togglePermission,
                toggleTeam,
                setTeams,
                clearPreview,
                isPreview,
            }}
        >
            {children}
        </DashboardRoleContext.Provider>
    );
}

export function useDashboardRole(): DashboardRoleContextValue {
    const context = useContext(DashboardRoleContext);

    if (!context) {
        return {
            role: DASHBOARD_ROLES.DEVELOPER,
            permissionCodes: [],
            teamIds: [],
            setPreviewRole: () => undefined,
            togglePermission: () => undefined,
            toggleTeam: () => undefined,
            setTeams: () => undefined,
            clearPreview: () => undefined,
            isPreview: false,
        };
    }

    return context;
}
